import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { ChevronRight } from "lucide-react";

export function BlogCard({ image, tag, tagStyle, date, title, href = "#" }) {
  return (
    <article className="bg-white rounded-2xl overflow-hidden shadow-sm border border-outline-variant/20 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
      <div className="relative overflow-hidden h-48">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className={`px-3 py-1 rounded-full text-[10px] font-bold font-manrope ${tagStyle}`}>
            {tag}
          </span>
          <span className="font-manrope text-body-sm text-on-surface-variant">{date}</span>
        </div>
        <h3 className="font-manrope text-h3 mb-4 line-clamp-2 group-hover:text-gold transition-colors">
          {title}
        </h3>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gold font-bold text-sm inline-flex items-center gap-1 group/link font-manrope"
        >
          Read More
          <span className="material-symbols-outlined text-sm group-hover/link:translate-x-1 transition-transform">
            <ChevronRight />
          </span>
        </a>
      </div>
    </article>
  );
}

export default function UpdatesSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const q = query(
          collection(db, "blogs"),
          orderBy("createdAt", "desc")
        );

        const snapshot = await getDocs(q);

        const blogs = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            id: doc.id,
            image: data.image,
            title: data.title,
            href: data.url,
            country: data.country,
            createdAt: data.createdAt,
          };
        });

        setPosts(blogs);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const getTagStyle = (country) => {
    switch (country?.toLowerCase()) {
      case "india":
        return "bg-secondary-container text-on-secondary-container";

      case "canada":
        return "bg-primary-fixed text-on-primary-fixed";

      case "us":
        return "bg-surface-variant text-on-surface-variant";

      default:
        return "bg-surface-variant text-on-surface-variant";
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    return timestamp
      .toDate()
      .toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
  };

  return (
    <section className="py-20 bg-surface-container-high/30" id="updates">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="flex justify-between items-end mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-gold" />
              <span className="font-manrope text-label-caps text-gold">STAY COMPLIANT</span>
            </div>
            <h2 className="font-manrope text-h1 text-navy">Latest Tax Updates</h2>
          </div>
          <button className="font-manrope font-bold text-sm text-navy flex items-center gap-2 hover:gap-4 hover:text-gold transition-all">
            View All Posts <span className="material-symbols-outlined"><ChevronRight /></span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-3 text-center py-12">
              Loading blogs...
            </div>
          ) : (
            posts.map((post) => (
              <BlogCard
                key={post.id}
                image={post.image}
                tag={post.country.toUpperCase()}
                tagStyle={getTagStyle(post.country)}
                date={formatDate(post.createdAt)}
                title={post.title}
                href={post.href}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}
